<script setup lang="ts">
import {ref, computed} from 'vue';
import type {JournalTemplateDTO, PostJournalRequest, JournalEntryDTO, MoodDTO} from '@/ts/types/dto/journals-dto';
import type {JournalFeelingOption} from '@/ts/types/component/today-page';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import JournalFeelingsField from '@/vue/molecules/journal-feelings-field.vue';
import MoodSelector from '@/vue/molecules/mood-selector.vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import {useFeelingSuggestions} from '@/ts/composables/pure/use-feeling-suggestions';

const props = defineProps<{
  template: JournalTemplateDTO;
}>();

const isOpen = defineModel<boolean>();

const emit = defineEmits<{
  save: [journal: JournalEntryDTO];
  cancel: [];
}>();

const logger = useLogger('JournalEntryModal');
const api = useApi();

const answerText = ref('');
const selectedMood = ref<MoodDTO | null>(null);

/**
 * The chosen feelings, as whole options rather than ids, because the picker draws their names.
 *
 * Only secondary feelings are on offer, so this is exactly what goes to `secondaryFeelingIds` —
 * the split into primary and secondary that used to live here went with the typeahead, which was
 * the only thing that ever offered a primary family as a choice.
 */
const selectedFeelings = ref<JournalFeelingOption[]>([]);

/** Derived the same way the Today tile derives them, from the same template call. */
const suggestions = computed(() => useFeelingSuggestions(props.template.availableFeelings));

/** The picker draws the search box; the draft it belongs to lives here. */
const feelingSearch = ref('');

function resetAndClose(): void {
  answerText.value = '';
  selectedFeelings.value = [];
  selectedMood.value = null;
  feelingSearch.value = '';
  isOpen.value = false;
}

const trimmedAnswer = computed(() => answerText.value.trim());

/** A mood on its own is a complete entry, and so is a note — but not neither (IPD-2973 §6.1). */
const canSave = computed(() => Boolean(selectedMood.value) || trimmedAnswer.value.length > 0);

const [saveJournal, isSaving] = useLoadingNotification(
  async() => {
    const request: PostJournalRequest = {
      contentId: props.template.journal.contentId,
      ...(trimmedAnswer.value.length > 0 && {answer: {answer: trimmedAnswer.value}}),
      ...(selectedFeelings.value.length > 0
        && {secondaryFeelingIds: selectedFeelings.value.map((feeling) => feeling.id)}),
      ...(selectedMood.value !== null && {mood: selectedMood.value}),
    };

    const savedJournal = await api.me.postJournal(request);
    logger.debug('Journal entry saved successfully')();
    return savedJournal;
  },
  (savedJournal) => {
    emit('save', savedJournal);
    resetAndClose();
  },
);

function handleSave(): void {
  if (!canSave.value) {
    logger.warn('Cannot save an entry with neither a mood nor a note')();
    return;
  }

  void saveJournal();
}

function handleCancel(): void {
  resetAndClose();
  emit('cancel');
}
</script>

<template>
  <balm-dialog v-model="isOpen" type="fit-content" theme="grey-10">
    <div class="journal-entry-modal">
      <h2 class="modal-title">
        {{ $t('new_entry') }}
      </h2>

      <!-- Mood selection (optional) -->
      <div class="mood-section">
        <p class="mood-label">
          {{ $t('journal_tile_mood_question') }}
        </p>
        <mood-selector v-model="selectedMood"/>
      </div>

      <!-- Prompt and answer -->
      <div class="prompt-section">
        <p class="journal-name">
          {{ template.journal.name }}
        </p>
        <q-input
          v-model="answerText"
          class="journal-textarea"
          type="textarea"
          borderless
          autogrow
          :placeholder="template.journal.prompt ?? ''"
        />
      </div>

      <!-- Feelings (optional): one row under the note, the picker a popover away. -->
      <journal-feelings-field
        v-if="template.availableFeelings.length > 0"
        v-model="selectedFeelings"
        v-model:search="feelingSearch"
        :feelings-by-mood="suggestions.byMood"
        :fallback-feelings="suggestions.fallback"
        :mood="selectedMood"
      />

      <div class="modal-actions">
        <balm-btn
          :label="$t('cancel')"
          theme="plain"
          :disable="isSaving"
          paddings="p2-5"
          @click="handleCancel"
        />
        <balm-btn
          :label="$t('save_entry')"
          theme="blue"
          :loading="isSaving"
          :disable="!canSave || isSaving"
          paddings="p2-5"
          @click="handleSave"
        />
      </div>
    </div>
  </balm-dialog>
</template>

<style scoped lang="scss">
.journal-entry-modal {
  display: flex;
  flex-direction: column;
  padding: 0 var(--Spacing-spacing-3) var(--Spacing-spacing-3);
  gap: var(--Spacing-spacing-6);

  @include screen-md {
    max-width: 800px;
  }

  @include screen-lg {
    min-width: 800px;
  }
}

.modal-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;
  padding-top: var(--Spacing-spacing-5);
  padding-right: var(--Spacing-spacing-8);

  @include screen-lg {
    padding-top: var(--Spacing-spacing-3);
  }
}

.prompt-section {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
}

.journal-name {
  @include font-h3;
  color: var(--Neutral-UI-neutral-ui-70);
  margin: 0;
}

.journal-textarea {
  @include q-input-textarea-0;

  :deep(textarea) {
    max-height: 200px;
    overflow-y: auto;
    overflow-wrap: break-word;

    @include screen-md {
      max-height: 300px;
    }
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--Spacing-spacing-3);
  margin-top: var(--Spacing-spacing-2);
}

.mood-section {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-2);
}

.mood-label {
  @include font-h3;
  color: var(--Neutral-UI-neutral-ui-70);
  margin: 0;
}

</style>
